import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { axiosPrivate } from "lib/config/axios";
import { ENDPOINTS } from "lib/constants";
import { toast } from "react-toastify";
import { DATA_FETCHING_STATUSES } from "lib/constants";

const initialState = {
  activeImage: null,
  loading: false,
  error: null,
};

// Thunk to upload image
export const uploadImageThunk = createAsyncThunk(
  "imageUpload/uploadImage",
  async (payload, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("backgroundImage", payload.file, payload.file.name);

      const response = await axiosPrivate.post(
        ENDPOINTS.BACKGROUND_IMAGE,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || "Failed to upload image";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// Thunk to fetch active background image
export const fetchActiveBackgroundImageThunk = createAsyncThunk(
  "imageUpload/fetchActiveImage",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosPrivate.get(ENDPOINTS.ACTIVE_BACKGROUND_IMAGE);
      return response.data;
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || "Failed to fetch active image";
      // Don't show error toast if no image exists
      if (error?.response?.status !== 404) {
        toast.error(errorMessage);
      }
      return rejectWithValue(errorMessage);
    }
  }
);

// Slice
const imageUploadSlice = createSlice({
  name: "imageUpload",
  initialState,
  reducers: {
    clearActiveImage: (state) => {
      state.activeImage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Upload Image
      .addCase(uploadImageThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadImageThunk.fulfilled, (state, action) => {
        state.loading = false;
        // Set as active image after upload
        if (action.payload.data) {
          state.activeImage = action.payload.data;
        }
      })
      .addCase(uploadImageThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Active Image
      .addCase(fetchActiveBackgroundImageThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchActiveBackgroundImageThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.activeImage = action.payload;
      })
      .addCase(fetchActiveBackgroundImageThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.activeImage = null;
      });
  },
});

export const { clearActiveImage } = imageUploadSlice.actions;

export default imageUploadSlice.reducer;

// Selector
export const useImageUploadSelector = () => {
  return (state) => state.imageUploadReducer;
};

